import type { ApiResponse } from '@vision/ui/interfaces';

export async function collectPages<T extends { id: string }>(
  fetchPage: (page: number) => Promise<ApiResponse<T[]>>,
  isCancelled: () => boolean = () => false,
): Promise<T[]> {
  const items = new Map<string, T>();
  let page = 1;

  while (!isCancelled()) {
    const response = await fetchPage(page);
    if (isCancelled()) return [];
    response.data.forEach((item) => items.set(item.id, item));
    const pagination = response.meta?.pagination;
    if (!pagination || page >= pagination.total_pages) break;
    page += 1;
  }

  return [...items.values()];
}

export async function collectPagesConcurrently<T extends { id: string }>(
  fetchPage: (page: number) => Promise<ApiResponse<T[]>>,
  isCancelled: () => boolean = () => false,
  concurrency = 4,
): Promise<T[]> {
  const firstResponse = await fetchPage(1);
  if (isCancelled()) return [];

  const totalPages = firstResponse.meta?.pagination?.total_pages ?? 1;
  const pageItems: T[][] = [firstResponse.data];
  let nextPage = 2;

  const fetchNextPage = async (): Promise<void> => {
    while (!isCancelled()) {
      const page = nextPage;
      nextPage += 1;
      if (page > totalPages) return;

      const response = await fetchPage(page);
      if (isCancelled()) return;
      pageItems[page - 1] = response.data;
    }
  };

  const workerCount = Math.min(Math.max(concurrency, 1), Math.max(totalPages - 1, 0));
  await Promise.all(Array.from({ length: workerCount }, fetchNextPage));
  if (isCancelled()) return [];

  const items = new Map<string, T>();
  pageItems.flat().forEach((item) => items.set(item.id, item));
  return [...items.values()];
}
